import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Clock, MapPin, Calendar as CalendarIcon, Loader2, Plus, AlertCircle } from 'lucide-react';
import { calculateDistance, calculateTravelFee, geocodeAddress } from '../services/mapService';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ServiceBooking = ({ product, store, onAddToCart, price, loading = false }) => {
    const { user, refreshUser, token } = useAuth();
    const [date, setDate] = useState(null);
    const [timeSlot, setTimeSlot] = useState(null);
    const [endTimeSlot, setEndTimeSlot] = useState(null);
    const [locationType, setLocationType] = useState('boutique'); // 'boutique' or 'client_home'

    // Address State
    const userAddresses = useMemo(() => user?.saved_addresses || [], [user]);
    const [selectedAddressId, setSelectedAddressId] = useState("");
    const [showNewAddress, setShowNewAddress] = useState(false);
    const [newAddress, setNewAddress] = useState({ address: "", city: "" });
    const [savingAddress, setSavingAddress] = useState(false);

    // Fee State
    const [travelFee, setTravelFee] = useState(0);
    const [travelDistance, setTravelDistance] = useState(0);
    const [calculatingFee, setCalculatingFee] = useState(false);

    // Slots
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    // Set initial selected address when addresses load or location changes to home
    useEffect(() => {
        if (locationType === 'client_home' && userAddresses.length > 0 && !selectedAddressId) {
            // Prioritize default address if it exists in the list
            const defaultAddr = userAddresses.find(a => a.id === user?.default_address_id);
            if (defaultAddr) {
                setSelectedAddressId(defaultAddr.id);
            } else {
                setSelectedAddressId(userAddresses[0].id || "addr-0");
            }
        }
    }, [locationType, userAddresses, user?.default_address_id, selectedAddressId]);

    // Calculate Fee when selected address changes
    useEffect(() => {
        const calculate = async () => {
            if (locationType !== 'client_home' || !selectedAddressId || !store?.coordinates) return;

            // Find selected address
            // Note: saved_addresses structure might vary, assuming ID or index
            const address = userAddresses.find((a, idx) => (a.id || `addr-${idx}`) === selectedAddressId);

            if (!address) return;

            setCalculatingFee(true);
            try {
                let coords = address.coordinates;

                // Geocode if missing
                if (!coords) {
                    const geo = await geocodeAddress(`${address.address}, ${address.city}`);
                    if (geo) {
                        coords = { lat: geo.lat, lon: geo.lon };
                        // Ideally update backend here, but for now just use locally
                    }
                }

                if (coords) {
                    const dist = calculateDistance(
                        coords.lat,
                        coords.lon,
                        store.coordinates.lat,
                        store.coordinates.lon
                    );
                    setTravelDistance(dist);

                    // Get fee settings from product details or store default
                    const pricePerKm = product.service_details?.price_per_km || 0;
                    const freeRadius = product.service_details?.free_zone_radius || 0;

                    const fee = calculateTravelFee(dist, pricePerKm, freeRadius);
                    setTravelFee(Math.round(fee)); // Rounded for simplicity
                } else {
                    toast.error("Impossible de localiser cette adresse pour le calcul des frais.");
                }
            } catch (e) {
                console.error(e);
            } finally {
                setCalculatingFee(false);
            }
        };

        calculate();
    }, [selectedAddressId, locationType, store, userAddresses, product]);


    // Fetch Slots logic (same as before)
    useEffect(() => {
        if (!date) {
            setAvailableSlots([]);
            return;
        }

        const fetchSlots = async () => {
            setLoadingSlots(true);
            try {
                const dateStr = format(date, 'yyyy-MM-dd');
                const response = await axios.get(`${API_URL}/products/${product.id}/slots`, {
                    params: { date: dateStr }
                });

                console.log('📅 Slots API Response:', response.data);

                // Handle both old format (string array) and new format (object array)
                let slots;
                if (response.data.length > 0 && typeof response.data[0] === 'string') {
                    // Old format: ["09:00", "10:00", ...]
                    console.log('⚠️ Using OLD slot format (strings)');
                    slots = response.data.map(timeStr => ({
                        formatted: timeStr,
                        available: true,  // All are available in old format
                        remaining: 1,
                        total: 1
                    }));
                } else {
                    // New format: [{time, available, total_capacity, remaining_capacity}]
                    console.log('✅ Using NEW slot format (objects)');
                    slots = response.data.map(slotObj => ({
                        formatted: slotObj.time,
                        available: slotObj.available === true,  // Explicit check
                        remaining: slotObj.remaining_capacity || 0,
                        total: slotObj.total_capacity || 1
                    }));
                }

                console.log('📊 Parsed slots:', slots);
                setAvailableSlots(slots);
                setTimeSlot(null);
                setEndTimeSlot(null);
            } catch (error) {
                console.error("❌ Error fetching slots:", error);
                toast.error("Impossible de charger les créneaux horaires");
                setAvailableSlots([]);
            } finally {
                setLoadingSlots(false);
            }
        };

        fetchSlots();
    }, [date, product.id]);

    // Handle New Address Save
    const handleSaveAddress = async () => {
        if (!newAddress.address || !newAddress.city) {
            toast.error("Adresse et ville requises");
            return;
        }
        setSavingAddress(true);
        try {
            // Geocode first
            const geo = await geocodeAddress(`${newAddress.address}, ${newAddress.city}`);

            const addressData = {
                id: `new-${Date.now()}`, // Temp ID, backend should generate real one
                address: newAddress.address,
                city: newAddress.city,
                coordinates: geo ? { lat: geo.lat, lon: geo.lon } : null
            };

            const newAddresses = [...userAddresses, addressData];
            await axios.put(`${API_URL}/users/profile`, {
                saved_addresses: newAddresses
            });

            await refreshUser();
            setSelectedAddressId(addressData.id);
            setShowNewAddress(false);
            setNewAddress({ address: "", city: "" });
            toast.success("Adresse ajoutée !");

        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de la sauvegarde de l'adresse");
        } finally {
            setSavingAddress(false);
        }
    };

    const handleAddToCart = () => {
        if (!date || !timeSlot) {
            toast.error("Veuillez sélectionner une date et une heure");
            return;
        }

        // 🔒 CRITICAL VALIDATION: Check if slot is available
        if (!timeSlot.available) {
            toast.error("❌ Ce créneau n'est plus disponible. Veuillez sélectionner un autre créneau.");
            return;
        }

        if (product.is_hourly && !endTimeSlot) {
            toast.error("Veuillez sélectionner une heure de fin");
            return;
        }

        // Check end slot availability for hourly services
        if (product.is_hourly && endTimeSlot && !endTimeSlot.available) {
            toast.error("❌ Le créneau de fin n'est plus disponible. Veuillez sélectionner une autre plage horaire.");
            return;
        }

        if (locationType === 'client_home' && !selectedAddressId) {
            toast.error("Veuillez sélectionner une adresse");
            return;
        }

        const startDt = new Date(date);
        const [sh, sm] = timeSlot.formatted.split(':');
        startDt.setHours(parseInt(sh), parseInt(sm), 0, 0);

        let endDt = null;
        if (product.is_hourly && endTimeSlot) {
            endDt = new Date(date);
            const [eh, em] = endTimeSlot.formatted.split(':');
            endDt.setHours(parseInt(eh), parseInt(em), 0, 0);
        } else {
            endDt = new Date(startDt.getTime() + (product.service_duration || 60) * 60000);
        }

        // Format datetime as YYYY-MM-DD HH:MM:SS in LOCAL timezone (NOT UTC)
        const formatLocalDateTime = (dt) => {
            const year = dt.getFullYear();
            const month = String(dt.getMonth() + 1).padStart(2, '0');
            const day = String(dt.getDate()).padStart(2, '0');
            const hours = String(dt.getHours()).padStart(2, '0');
            const minutes = String(dt.getMinutes()).padStart(2, '0');
            const seconds = String(dt.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        const booking = {
            bookingDate: date,
            bookingTime: timeSlot.formatted,
            booking_start: formatLocalDateTime(startDt),  // Local time, not UTC
            booking_end: formatLocalDateTime(endDt),      // Local time, not UTC
            serviceLocation: locationType,
            isService: true,
            travelFee: locationType === 'client_home' ? travelFee : 0,
            travelDistance: locationType === 'client_home' ? travelDistance : 0,
            deliveryAddress: locationType === 'client_home'
                ? userAddresses.find((a, idx) => (a.id || `addr-${idx}`) === selectedAddressId)
                : null
        };

        onAddToCart(booking);
    };

    const durationHours = useMemo(() => {
        if (!product.is_hourly) return 1;
        if (!timeSlot || !endTimeSlot) return 0;

        const [sh, sm] = timeSlot.formatted.split(':').map(Number);
        const [eh, em] = endTimeSlot.formatted.split(':').map(Number);
        const startTotalMinutes = sh * 60 + sm;
        const endTotalMinutes = eh * 60 + em;

        return Math.max(0, (endTotalMinutes - startTotalMinutes) / 60);
    }, [timeSlot, endTimeSlot, product.is_hourly]);

    const total = (price * (durationHours || 1)) + (locationType === 'client_home' ? travelFee : 0);

    return (
        <div className="space-y-6">
            {/* 1. Date Selection */}
            <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-orange-500" />
                    Choisir une date
                </h3>
                <div className="border rounded-lg p-2 bg-white flex justify-center">
                    <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                            setDate(d);
                            setTimeSlot(null);
                        }}
                        locale={fr}
                        className="rounded-md border-0"
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        initialFocus
                    />
                </div>
            </div>

            {/* 2. Slot Selection */}
            {date && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-4">
                    <h3 className="font-medium flex items-center gap-2">
                        <Clock className="w-5 h-5 text-orange-500" />
                        {product.is_hourly === true ? "Plage de réservation" : "Choisir un créneau"}
                    </h3>
                    {loadingSlots ? (
                        <div className="flex justify-center p-4"><Loader2 className="animate-spin" /></div>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-gray-400 uppercase font-bold">Heure de début</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {availableSlots.map((slot) => (
                                        <Button
                                            key={slot.formatted}
                                            variant={timeSlot?.formatted === slot.formatted ? "default" : "outline"}
                                            className={`text-sm relative ${!slot.available
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                                                : timeSlot?.formatted === slot.formatted
                                                    ? 'bg-orange-500 hover:bg-orange-600'
                                                    : ''
                                                }`}
                                            disabled={!slot.available}
                                            onClick={() => {
                                                if (!slot.available) return;
                                                setTimeSlot(slot);
                                                if (endTimeSlot && availableSlots.indexOf(slot) >= availableSlots.indexOf(endTimeSlot)) {
                                                    setEndTimeSlot(null);
                                                }
                                            }}
                                        >
                                            <span className="flex items-center gap-1">
                                                {slot.formatted}
                                                {!slot.available && (
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {product.is_hourly && timeSlot && (
                                <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                    <Label className="text-xs text-orange-600 uppercase font-bold">Heure de fin de service</Label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {availableSlots
                                            .filter((_, idx) => idx > availableSlots.indexOf(timeSlot))
                                            .map((slot) => (
                                                <Button
                                                    key={slot.formatted}
                                                    variant={endTimeSlot?.formatted === slot.formatted ? "default" : "outline"}
                                                    className={`text-sm relative ${!slot.available
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                                                        : endTimeSlot?.formatted === slot.formatted
                                                            ? 'bg-orange-500 hover:bg-orange-600'
                                                            : ''
                                                        }`}
                                                    disabled={!slot.available}
                                                    onClick={() => slot.available && setEndTimeSlot(slot)}
                                                >
                                                    <span className="flex items-center gap-1">
                                                        {slot.formatted}
                                                        {!slot.available && (
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </span>
                                                </Button>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {availableSlots.length === 0 && (
                                <p className="text-sm text-gray-500">Aucun créneau disponible.</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* 3. Location Selection */}
            <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    Lieu du service
                </h3>
                <RadioGroup value={locationType} onValueChange={setLocationType} className="flex flex-col space-y-2">
                    {/* Check product available options */}
                    {['both', 'provider_shop'].includes(product.service_location_type || 'both') && (
                        <div className={`flex items-center space-x-2 border p-3 rounded-md cursor-pointer hover:bg-gray-50 bg-white ${locationType === 'boutique' ? 'border-orange-500 ring-1 ring-orange-500' : ''}`}>
                            <RadioGroupItem value="boutique" id="r1" />
                            <Label htmlFor="r1" className="flex-1 cursor-pointer">
                                À la boutique
                                <span className="block text-xs text-gray-500 font-normal">Vous vous déplacez chez l'artisan</span>
                            </Label>
                        </div>
                    )}

                    {['both', 'client_home'].includes(product.service_location_type || 'both') && (
                        <div className={`flex items-center space-x-2 border p-3 rounded-md cursor-pointer hover:bg-gray-50 bg-white ${locationType === 'client_home' ? 'border-orange-500 ring-1 ring-orange-500' : ''}`}>
                            <RadioGroupItem value="client_home" id="r2" />
                            <Label htmlFor="r2" className="flex-1 cursor-pointer">
                                À domicile
                                <span className="block text-xs text-gray-500 font-normal">L'artisan se déplace chez vous</span>
                            </Label>
                        </div>
                    )}
                </RadioGroup>

                {/* Address Selector for Home Service */}
                {locationType === 'client_home' && (
                    <div className="pl-4 border-l-2 border-orange-100 space-y-3 animate-in fade-in">
                        {!showNewAddress ? (
                            <>
                                {userAddresses.length > 0 ? (
                                    <div className="space-y-2">
                                        <Label>Sélectionnez votre adresse</Label>
                                        <Select value={selectedAddressId} onValueChange={setSelectedAddressId}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Choisir une adresse" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {userAddresses.map((addr, idx) => (
                                                    <SelectItem key={addr.id || `addr-${idx}`} value={addr.id || `addr-${idx}`}>
                                                        {addr.address}, {addr.city}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-orange-600 p-0 h-auto font-normal"
                                            onClick={() => setShowNewAddress(true)}
                                        >
                                            <Plus className="w-3 h-3 mr-1" /> Ajouter une nouvelle adresse
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-center p-4 border border-dashed rounded bg-gray-50">
                                        <p className="text-sm text-gray-500 mb-2">Aucune adresse enregistrée</p>
                                        <Button variant="outline" size="sm" onClick={() => setShowNewAddress(true)}>
                                            Ajouter une adresse
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="space-y-3 bg-gray-50 p-3 rounded">
                                <Label>Nouvelle adresse</Label>
                                <Input
                                    placeholder="Numéro et rue"
                                    value={newAddress.address}
                                    onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                                />
                                <Input
                                    placeholder="Ville"
                                    value={newAddress.city}
                                    onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                                />
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={handleSaveAddress} disabled={savingAddress}>
                                        {savingAddress ? <Loader2 className="w-3 h-3 animate-spin" /> : "Enregistrer"}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setShowNewAddress(false)}>Annuler</Button>
                                </div>
                            </div>
                        )}

                        {/* Fee Display */}
                        <div className="mt-2 text-sm">
                            {calculatingFee ? (
                                <span className="flex items-center text-gray-500"><Loader2 className="w-3 h-3 animate-spin mr-1" /> Calcul des frais...</span>
                            ) : travelFee > 0 ? (
                                <div className="flex items-center text-orange-700 bg-orange-50 p-2 rounded">
                                    <AlertCircle className="w-4 h-4 mr-2" />
                                    <span>
                                        Frais de déplacement: <strong>{travelFee} HTG</strong>
                                        <span className="text-xs text-gray-500 block">({travelDistance.toFixed(1)} km)</span>
                                    </span>
                                </div>
                            ) : selectedAddressId && travelDistance > 0 ? (
                                <span className="text-green-600 text-xs">Frais offerts (Zone gratuite)</span>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>

            <div className="pt-4 border-t">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-600">Total estimé</span>
                    <div className="text-right">
                        <span className="text-xl font-bold text-orange-600">
                            {total.toFixed(2)} HTG
                        </span>
                        {product.is_hourly && durationHours > 0 && (
                            <p className="text-xs text-gray-500">
                                {durationHours} heure{durationHours > 1 ? 's' : ''} à {price} HTG/h
                            </p>
                        )}
                        {travelFee > 0 && <p className="text-xs text-gray-500">dont {travelFee} HTG de frais</p>}
                    </div>
                </div>
                <Button
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
                    onClick={handleAddToCart}
                    disabled={!date || !timeSlot || (product.is_hourly && !endTimeSlot) || loading || (locationType === 'client_home' && !selectedAddressId)}
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    Réserver maintenant
                </Button>
                <p className="text-center text-xs text-gray-500 mt-2">
                    Paiement requis pour confirmer le créneau
                </p>
            </div>
        </div>
    );
};

export default ServiceBooking;
